/**
 * Procedural sky dome. Owned by `look`.
 *
 * A single back-faced sphere with a shader that draws:
 *   · a zenith→horizon→haze gradient (the Crazy Taxi blue, and it must survive tonemapping)
 *   · a below-horizon ground fill so a low camera never sees a hole
 *   · a sun disc + falloff halo
 *   · a fat cumulus band from 3-octave hash noise, drifting on ctx.time.sim
 *
 * Costs exactly one draw call and one program. Nothing here is animated by wall-clock time,
 * so a frozen shot is bit-identical (ARCHITECTURE.md §7.3).
 *
 * The same material is also rendered into a private scene by the PMREM generator to produce
 * `scene.environment`, which is what makes a wet road at night actually reflect the sky.
 */

import * as THREE from 'three';
import { LOOK_TUNING as L } from './tuning.js';

const VERT = /* glsl */`
varying vec3 vDir;
void main() {
  // Direction from the camera through this vertex, in world space.
  vec4 wp = modelMatrix * vec4(position, 1.0);
  vDir = normalize(wp.xyz - cameraPosition);
  gl_Position = projectionMatrix * viewMatrix * wp;
  // Pin to the far plane so the dome can never clip into geometry.
  gl_Position.z = gl_Position.w;
}
`;

const FRAG = /* glsl */`
precision highp float;

varying vec3 vDir;

uniform vec3  uZenith;
uniform vec3  uMid;
uniform vec3  uHorizon;
uniform vec3  uHaze;
uniform vec3  uGround;
uniform vec3  uCloud;
uniform vec3  uCloudShade;
uniform vec3  uSunColor;
uniform vec3  uSunDir;

uniform float uGradientPower;
uniform float uHorizonSoft;
uniform float uGain;
uniform float uSunSize;
uniform float uSunGlowPower;
uniform float uSunGlowStrength;
uniform float uCloudAmount;
uniform float uCloudSharp;
uniform float uCloudScale;
uniform float uCloudOpacity;
uniform float uCloudDrift;

// Deterministic hash — integer ops only, identical on every GPU we care about.
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float fbm3(vec2 p) {
  float s = 0.0;
  s += vnoise(p) * 0.5;
  s += vnoise(p * 2.03 + 17.1) * 0.25;
  s += vnoise(p * 4.11 + 41.7) * 0.125;
  return s / 0.875;
}

void main() {
  vec3 d = normalize(vDir);
  float y = d.y;

  // ── vertical gradient ────────────────────────────────────────────────────────────
  float t = clamp(y, 0.0, 1.0);
  float g = pow(t, uGradientPower);
  vec3 sky = mix(uHorizon, uMid, clamp(g * 2.0, 0.0, 1.0));
  sky = mix(sky, uZenith, clamp((g - 0.5) * 2.0, 0.0, 1.0));

  // Pale haze band sitting right on the horizon line.
  float hz = 1.0 - smoothstep(0.0, uHorizonSoft, max(y, 0.0));
  sky = mix(sky, uHaze, hz * 0.92);

  // ── clouds ───────────────────────────────────────────────────────────────────────
  // Project the view direction onto a plane above the camera. Guarding the divisor keeps
  // the cloud layer from exploding at the horizon.
  float yy = max(y, 0.055);
  vec2 cp = d.xz / yy * uCloudScale + vec2(uCloudDrift, uCloudDrift * 0.37);
  float n = fbm3(cp * 0.45);
  float cover = smoothstep(1.0 - uCloudAmount, 1.0 - uCloudAmount + 0.42 / uCloudSharp, n);
  // Fade clouds out toward the horizon so the band does not turn into a grey wall.
  cover *= smoothstep(0.02, 0.30, y);
  // Shade the underside using the noise derivative-ish (a second, offset sample).
  float n2 = fbm3(cp * 0.45 + vec2(0.0, -0.16));
  vec3 cloudCol = mix(uCloudShade, uCloud, clamp((n - n2) * 3.0 + 0.55, 0.0, 1.0));
  sky = mix(sky, cloudCol, cover * uCloudOpacity);

  // ── sun ──────────────────────────────────────────────────────────────────────────
  float sd = max(dot(d, normalize(uSunDir)), 0.0);
  float halo = pow(sd, uSunGlowPower) * uSunGlowStrength;
  float disc = smoothstep(1.0 - uSunSize, 1.0 - uSunSize * 0.35, sd);
  sky += uSunColor * halo;
  sky = mix(sky, uSunColor * 2.4, disc);

  // ── below the horizon ────────────────────────────────────────────────────────────
  sky = mix(sky, uGround, smoothstep(0.0, -0.10, y));

  gl_FragColor = vec4(sky * uGain, 1.0);

  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export class Sky {
  constructor() {
    this.uniforms = {
      uZenith: { value: new THREE.Color(0x2A62EE) },
      uMid: { value: new THREE.Color(0x4582FB) },
      uHorizon: { value: new THREE.Color(0x8DC3FC) },
      uHaze: { value: new THREE.Color(0xC6E2F7) },
      uGround: { value: new THREE.Color(0x7E8B93) },
      uCloud: { value: new THREE.Color(0xF4FAFD) },
      uCloudShade: { value: new THREE.Color(0xA8C4DE) },
      uSunColor: { value: new THREE.Color(0xFFF3D2) },
      uSunDir: { value: new THREE.Vector3(0.45, 0.72, 0.52) },

      uGradientPower: { value: L.sky.gradientPower.thaw },
      uHorizonSoft: { value: L.sky.horizonSoft.thaw },
      uGain: { value: L.sky.gain.thaw },
      uSunSize: { value: L.sky.sunSize.thaw },
      uSunGlowPower: { value: L.sky.sunGlowPower.thaw },
      uSunGlowStrength: { value: L.sky.sunGlowStrength.thaw },
      uCloudAmount: { value: L.sky.cloudAmount.thaw },
      uCloudSharp: { value: L.sky.cloudSharpness.thaw },
      uCloudScale: { value: L.sky.cloudScale.thaw },
      uCloudOpacity: { value: L.sky.cloudOpacity.thaw },
      uCloudDrift: { value: 0 },
    };

    this.material = new THREE.ShaderMaterial({
      uniforms: this.uniforms,
      vertexShader: VERT,
      fragmentShader: FRAG,
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      fog: false,
      toneMapped: true,
    });
    this.material.name = 'sky.dome';

    this.geometry = new THREE.SphereGeometry(L.sky.radius, L.sky.segmentsW, L.sky.segmentsH);

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.name = 'sky';
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -10000;
    this.mesh.matrixAutoUpdate = false;

    /**
     * A second mesh sharing the same geometry + material, parked in a private scene that the
     * PMREM generator renders. Cannot reuse `mesh` — a THREE.Object3D has one parent.
     */
    this.envMesh = new THREE.Mesh(this.geometry, this.material);
    this.envMesh.frustumCulled = false;
    this.envScene = new THREE.Scene();
    this.envScene.add(this.envMesh);
  }

  /** Keep the dome centred on the camera so it never clips or parallaxes. */
  followCamera(camera) {
    this.mesh.position.copy(camera.position);
    this.mesh.updateMatrix();
    this.mesh.updateMatrixWorld(true);
  }

  /** Drift the cloud band. `simTime` must be ctx.time.sim so a frozen shot is identical. */
  setTime(simTime) {
    this.uniforms.uCloudDrift.value = simTime * L.sky.cloudDriftPerSecond;
  }

  setSunDirection(v) { this.uniforms.uSunDir.value.copy(v); }

  /** The colour that fog and the clear buffer should match — the horizon haze band. */
  horizonColor(out) {
    return out.copy(this.uniforms.uHaze.value).lerp(this.uniforms.uHorizon.value, 0.42)
      .multiplyScalar(this.uniforms.uGain.value);
  }

  dispose() {
    this.geometry.dispose();
    this.material.dispose();
    this.envScene.clear();
  }
}

export default Sky;
